import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, MapPin, Truck, Store, CreditCard, Wallet, 
  Smartphone, Loader2, CheckCircle2, Minus, Plus, Trash2,
  Clock, Percent, ChevronRight, Navigation, Phone, Building,
  Banknote, AlertCircle, Shield, Calendar
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_aimarketplace-18/artifacts/7akyhfla_IMG_5649.jpg';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 0,
  }).format(amount);
};

export default function CheckoutPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, token, user } = useAuth();
  
  const [cartItems, setCartItems] = useState([]);
  const [cartData, setCartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [stores, setStores] = useState([]);
  const [gettingLocation, setGettingLocation] = useState(false);
  
  // Form state
  const [deliveryType, setDeliveryType] = useState('delivery');
  const [selectedStore, setSelectedStore] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('mpesa');
  const [useFinancing, setUseFinancing] = useState(false);
  const [financingMonths, setFinancingMonths] = useState(3);
  const [address, setAddress] = useState({
    full_name: user?.name || '',
    phone: user?.phone || '',
    address_line1: '',
    address_line2: '',
    city: 'Nairobi',
    area: '',
    postal_code: '',
    latitude: null,
    longitude: null,
  });

  // M-Pesa state
  const [mpesaPhone, setMpesaPhone] = useState(user?.phone || '');
  const [showMpesaModal, setShowMpesaModal] = useState(false);
  const [mpesaCheckoutId, setMpesaCheckoutId] = useState(null);
  const [mpesaStatus, setMpesaStatus] = useState('pending');

  // M-Shwari state
  const [mshwariEligibility, setMshwariEligibility] = useState(null);
  const [showMshwariModal, setShowMshwariModal] = useState(false);
  const [checkingEligibility, setCheckingEligibility] = useState(false);

  useEffect(() => {
    const savedCart = localStorage.getItem('jambo_cart');
    if (savedCart) {
      setCartItems(JSON.parse(savedCart));
    } else if (location.state?.cart) {
      setCartItems(location.state.cart);
    }
    fetchStores();
  }, []);

  useEffect(() => {
    if (cartItems.length > 0) {
      calculateCart();
    } else {
      setLoading(false);
    }
  }, [cartItems]);

  // Poll M-Pesa status
  useEffect(() => {
    if (mpesaCheckoutId && mpesaStatus === 'pending') {
      const interval = setInterval(async () => {
        try {
          const response = await axios.get(`${API_URL}/api/payments/mpesa/status/${mpesaCheckoutId}`);
          if (response.data.status === 'completed') {
            setMpesaStatus('completed');
            clearInterval(interval);
            toast.success('Payment successful!');
            setTimeout(() => {
              completeOrder(response.data.mpesa_receipt);
            }, 1500);
          } else if (response.data.status === 'failed') {
            setMpesaStatus('failed');
            clearInterval(interval);
            toast.error('Payment failed. Please try again.');
          }
        } catch (error) {
          console.error('Error checking payment status:', error);
        }
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [mpesaCheckoutId, mpesaStatus]);

  const fetchStores = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stores`);
      setStores(response.data.stores);
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  const calculateCart = async () => {
    try {
      const items = cartItems.map(item => ({
        product_id: item.id,
        product_type: item.product_type || 'product',
        quantity: item.quantity || 1
      }));
      const response = await axios.post(`${API_URL}/api/cart/calculate`, items);
      setCartData(response.data);
    } catch (error) {
      console.error('Error calculating cart:', error);
      toast.error('Failed to calculate cart');
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = (index, change) => {
    const newCart = [...cartItems];
    newCart[index].quantity = Math.max(1, (newCart[index].quantity || 1) + change);
    setCartItems(newCart);
    localStorage.setItem('jambo_cart', JSON.stringify(newCart));
  };

  const removeItem = (index) => {
    const newCart = cartItems.filter((_, i) => i !== index);
    setCartItems(newCart);
    localStorage.setItem('jambo_cart', JSON.stringify(newCart));
    if (newCart.length === 0) navigate('/');
  };

  const handleGetLocation = async () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setAddress(prev => ({ ...prev, latitude, longitude }));
        
        // Reverse geocode to get address
        try {
          const response = await axios.post(`${API_URL}/api/location/reverse-geocode`, {
            latitude, longitude
          });
          if (response.data.success) {
            const addr = response.data.address;
            setAddress(prev => ({
              ...prev,
              address_line1: addr.formatted_address,
              city: addr.city,
              area: addr.area,
              postal_code: addr.postal_code
            }));
            toast.success('Location captured! Address auto-filled.');
          }
        } catch (error) {
          toast.success('Location captured!');
        }
        
        // Also get nearest stores
        try {
          const nearestRes = await axios.get(`${API_URL}/api/stores/nearest?lat=${latitude}&lng=${longitude}&limit=3`);
          if (nearestRes.data.stores) {
            setStores(prev => {
              const nearestIds = nearestRes.data.stores.map(s => s.id);
              const sorted = [...nearestRes.data.stores, ...prev.filter(s => !nearestIds.includes(s.id))];
              return sorted;
            });
          }
        } catch (error) {
          console.error('Error getting nearest stores:', error);
        }
        
        setGettingLocation(false);
      },
      (error) => {
        toast.error('Unable to get your location');
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const initiateMpesaPayment = async () => {
    if (!mpesaPhone || mpesaPhone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(`${API_URL}/api/payments/mpesa/initiate`, {
        phone_number: mpesaPhone,
        amount: finalTotal,
        description: `Jambo Order - ${cartItems.length} items`
      });
      
      setMpesaCheckoutId(response.data.checkout_request_id);
      setMpesaStatus('pending');
      setShowMpesaModal(true);
      toast.info('Check your phone for M-Pesa prompt');
    } catch (error) {
      toast.error('Failed to initiate M-Pesa payment');
    } finally {
      setProcessing(false);
    }
  };

  const checkMshwariEligibility = async () => {
    setCheckingEligibility(true);
    try {
      const response = await axios.post(`${API_URL}/api/payments/mshwari/check-eligibility`, {
        phone_number: mpesaPhone || user?.phone,
        amount: finalTotal
      });
      setMshwariEligibility(response.data);
      setShowMshwariModal(true);
    } catch (error) {
      toast.error('Failed to check M-Shwari eligibility');
    } finally {
      setCheckingEligibility(false);
    }
  };

  const requestMshwariLoan = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API_URL}/api/payments/mshwari/request-loan`, {
        phone_number: mpesaPhone || user?.phone,
        amount: finalTotal
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setShowMshwariModal(false);
        completeOrder(response.data.loan_id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process M-Shwari loan');
    } finally {
      setProcessing(false);
    }
  };

  const completeOrder = async (paymentRef = null) => {
    if (!isAuthenticated) {
      localStorage.setItem('jambo_cart', JSON.stringify(cartItems));
      navigate('/auth', { state: { from: { pathname: '/checkout' } } });
      return;
    }

    setProcessing(true);
    try {
      const checkoutData = {
        cart_items: cartItems.map(item => ({
          product_id: item.id,
          product_type: item.product_type || 'product',
          quantity: item.quantity || 1
        })),
        delivery_address: {
          ...address,
          delivery_type: deliveryType,
          pickup_store: deliveryType === 'pickup' ? selectedStore : null
        },
        payment_method: paymentMethod,
        payment_reference: paymentRef,
        use_financing: useFinancing,
        financing_months: useFinancing ? financingMonths : 0
      };

      const response = await axios.post(`${API_URL}/api/checkout`, checkoutData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      localStorage.removeItem('jambo_cart');
      setShowMpesaModal(false);
      toast.success('Order placed successfully!');
      navigate('/dashboard', { state: { orderSuccess: true, orderId: response.data.order_id } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      localStorage.setItem('jambo_cart', JSON.stringify(cartItems));
      navigate('/auth', { state: { from: { pathname: '/checkout' } } });
      return;
    }

    if (deliveryType === 'delivery' && !address.address_line1) {
      toast.error('Please enter your delivery address');
      return;
    }

    if (deliveryType === 'pickup' && !selectedStore) {
      toast.error('Please select a pickup store');
      return;
    }

    if (paymentMethod === 'mpesa') {
      initiateMpesaPayment();
    } else if (paymentMethod === 'mshwari') {
      checkMshwariEligibility();
    } else {
      completeOrder();
    }
  };

  const getSelectedFinancing = () => {
    if (!useFinancing || !cartData?.financing_options) return null;
    return cartData.financing_options.find(o => o.months === financingMonths);
  };

  const selectedFinancing = getSelectedFinancing();
  const finalTotal = selectedFinancing ? selectedFinancing.total_amount : cartData?.total || 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <h2 className="text-xl font-bold mb-4">Your cart is empty</h2>
        <Link to="/"><Button className="rounded-full jambo-gradient text-white">Continue Shopping</Button></Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-border/50">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-muted rounded-full">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <img src={LOGO_URL} alt="Jambo" className="h-8" />
            <h1 className="font-bold text-lg">Checkout</h1>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Cart Items */}
            <Card data-testid="cart-items">
              <CardHeader>
                <CardTitle className="text-lg">Your Items ({cartItems.length})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {cartData?.items?.map((item, index) => (
                  <div key={index} className="flex gap-4 p-3 rounded-xl bg-muted/50">
                    <img 
                      src={item.product?.image_url || 'https://via.placeholder.com/80'} 
                      alt={item.product?.name || item.product?.title}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <h3 className="font-medium">{item.product?.name || item.product?.title}</h3>
                      <p className="text-sm text-muted-foreground">{item.product?.category || item.product?.location}</p>
                      <p className="font-bold text-cyan-600 mt-1">{formatCurrency(item.product?.price)}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <button onClick={() => removeItem(index)} className="text-red-500 hover:bg-red-50 p-1 rounded">
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <div className="flex items-center gap-2">
                        <button onClick={() => updateQuantity(index, -1)} className="w-7 h-7 rounded-full bg-muted flex items-center justify-center">
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="w-8 text-center font-medium">{item.quantity}</span>
                        <button onClick={() => updateQuantity(index, 1)} className="w-7 h-7 rounded-full bg-muted flex items-center justify-center">
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Delivery Options */}
            <Card data-testid="delivery-options">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Truck className="h-5 w-5 text-cyan-500" />
                  Delivery Options
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup value={deliveryType} onValueChange={setDeliveryType}>
                  <div className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${deliveryType === 'delivery' ? 'border-cyan-500 bg-cyan-50' : 'border-border'}`}>
                    <RadioGroupItem value="delivery" id="delivery" />
                    <Truck className="h-5 w-5 text-cyan-500" />
                    <div className="flex-1">
                      <Label htmlFor="delivery" className="font-medium cursor-pointer">Home Delivery</Label>
                      <p className="text-sm text-muted-foreground">Delivered to your doorstep (2-4 days)</p>
                    </div>
                    <span className="text-sm font-medium">{cartData?.subtotal > 5000 ? <Badge className="bg-green-500">FREE</Badge> : formatCurrency(300)}</span>
                  </div>
                  <div className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${deliveryType === 'pickup' ? 'border-green-500 bg-green-50' : 'border-border'}`}>
                    <RadioGroupItem value="pickup" id="pickup" />
                    <Store className="h-5 w-5 text-green-500" />
                    <div className="flex-1">
                      <Label htmlFor="pickup" className="font-medium cursor-pointer">Store Pickup</Label>
                      <p className="text-sm text-muted-foreground">Ready in 24 hours</p>
                    </div>
                    <Badge className="bg-green-500">FREE</Badge>
                  </div>
                </RadioGroup>

                {deliveryType === 'delivery' && (
                  <div className="space-y-4 pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">Delivery Address</h4>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleGetLocation} 
                        className="rounded-full"
                        disabled={gettingLocation}
                      >
                        {gettingLocation ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Navigation className="h-4 w-4 mr-1" />
                        )}
                        Use My Location
                      </Button>
                    </div>
                    
                    {address.latitude && (
                      <div className="p-3 rounded-xl bg-green-50 border border-green-200 flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-green-700">Location captured</p>
                          <p className="text-xs text-green-600">{address.area || 'Coordinates'}: {address.latitude?.toFixed(4)}, {address.longitude?.toFixed(4)}</p>
                        </div>
                      </div>
                    )}
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <Input placeholder="Full Name *" value={address.full_name} onChange={e => setAddress({...address, full_name: e.target.value})} />
                      <Input placeholder="Phone Number *" value={address.phone} onChange={e => setAddress({...address, phone: e.target.value})} />
                    </div>
                    <Input 
                      placeholder="Address Line 1 (Street, Building) *" 
                      value={address.address_line1} 
                      onChange={e => setAddress({...address, address_line1: e.target.value})} 
                      data-testid="address-input" 
                    />
                    <Input placeholder="Address Line 2 (Apartment, Floor - Optional)" value={address.address_line2} onChange={e => setAddress({...address, address_line2: e.target.value})} />
                    <div className="grid grid-cols-3 gap-4">
                      <Input placeholder="Area/Estate" value={address.area} onChange={e => setAddress({...address, area: e.target.value})} />
                      <Input placeholder="City" value={address.city} onChange={e => setAddress({...address, city: e.target.value})} />
                      <Input placeholder="Postal Code" value={address.postal_code} onChange={e => setAddress({...address, postal_code: e.target.value})} />
                    </div>
                  </div>
                )}

                {deliveryType === 'pickup' && (
                  <div className="space-y-3 pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">Select Pickup Store</h4>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={handleGetLocation}
                        disabled={gettingLocation}
                        className="text-cyan-600"
                      >
                        {gettingLocation ? <Loader2 className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />}
                        <span className="ml-1">Find Nearest</span>
                      </Button>
                    </div>
                    
                    <div className="grid gap-3">
                      {stores.map((store, index) => (
                        <div 
                          key={store.id}
                          onClick={() => setSelectedStore(store.id)}
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-colors ${selectedStore === store.id ? 'border-green-500 bg-green-50' : 'border-border hover:bg-muted/50'}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedStore === store.id ? 'bg-green-500 text-white' : 'bg-muted'}`}>
                                <Building className="h-5 w-5" />
                              </div>
                              <div>
                                <div className="font-medium flex items-center gap-2">
                                  {store.name}
                                  {index === 0 && store.distance && (
                                    <Badge variant="outline" className="text-xs">Nearest</Badge>
                                  )}
                                </div>
                                <div className="text-sm text-muted-foreground">{store.address}</div>
                                <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {store.hours}</span>
                                  <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {store.phone}</span>
                                </div>
                              </div>
                            </div>
                            {store.distance && (
                              <Badge variant="secondary" className="text-xs">
                                {store.distance.toFixed(1)} km
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Financing Options */}
            <Card data-testid="financing-options">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-orange-500" />
                  Pay in Installments
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div 
                  onClick={() => setUseFinancing(!useFinancing)}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-colors ${useFinancing ? 'border-orange-500 bg-orange-50' : 'border-border'}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${useFinancing ? 'bg-orange-500 text-white' : 'bg-muted'}`}>
                        <Percent className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-medium">Enable Installment Payments</p>
                        <p className="text-sm text-muted-foreground">Split your payment over 3-24 months</p>
                      </div>
                    </div>
                    <div className={`w-12 h-6 rounded-full transition-colors ${useFinancing ? 'bg-orange-500' : 'bg-muted'} relative`}>
                      <div className={`absolute w-5 h-5 rounded-full bg-white shadow top-0.5 transition-all ${useFinancing ? 'right-0.5' : 'left-0.5'}`} />
                    </div>
                  </div>
                </div>

                {useFinancing && cartData?.financing_options && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {cartData.financing_options.map(option => (
                      <div
                        key={option.months}
                        onClick={() => setFinancingMonths(option.months)}
                        className={`p-4 rounded-xl border-2 cursor-pointer text-center transition-colors ${financingMonths === option.months ? 'border-orange-500 bg-orange-50' : 'border-border hover:bg-muted/50'}`}
                      >
                        <p className="text-2xl font-bold text-orange-600">{option.months}</p>
                        <p className="text-xs text-muted-foreground mb-2">months</p>
                        <p className="font-bold">{formatCurrency(option.monthly_payment)}</p>
                        <p className="text-xs text-muted-foreground">/month</p>
                        <Badge variant="secondary" className="mt-2 text-xs">{option.interest_rate}% interest</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payment Methods */}
            <Card data-testid="payment-method">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-purple-500" />
                  Payment Method
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-3">
                  {/* M-Pesa */}
                  <div className={`p-4 rounded-xl border-2 cursor-pointer ${paymentMethod === 'mpesa' ? 'border-green-500 bg-green-50' : 'border-border'}`}>
                    <div className="flex items-center gap-4">
                      <RadioGroupItem value="mpesa" id="mpesa" />
                      <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                        <img src="https://upload.wikimedia.org/wikipedia/commons/b/b5/M-PESA_LOGO-01.svg" alt="M-Pesa" className="h-6" />
                      </div>
                      <div className="flex-1">
                        <Label htmlFor="mpesa" className="font-medium cursor-pointer">M-Pesa</Label>
                        <p className="text-sm text-muted-foreground">Pay instantly via mobile money</p>
                      </div>
                      <Badge className="bg-green-500">Popular</Badge>
                    </div>
                    {paymentMethod === 'mpesa' && (
                      <div className="mt-4 pt-4 border-t">
                        <Label className="text-sm text-muted-foreground">M-Pesa Phone Number</Label>
                        <Input 
                          placeholder="+254 7XX XXX XXX"
                          value={mpesaPhone}
                          onChange={e => setMpesaPhone(e.target.value)}
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">You'll receive an STK push to enter your PIN</p>
                      </div>
                    )}
                  </div>

                  {/* M-Shwari */}
                  <div className={`p-4 rounded-xl border-2 cursor-pointer ${paymentMethod === 'mshwari' ? 'border-blue-500 bg-blue-50' : 'border-border'}`}>
                    <div className="flex items-center gap-4">
                      <RadioGroupItem value="mshwari" id="mshwari" />
                      <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                        <Banknote className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <Label htmlFor="mshwari" className="font-medium cursor-pointer">M-Shwari</Label>
                        <p className="text-sm text-muted-foreground">Buy now, pay in 30 days</p>
                      </div>
                      <Badge variant="outline" className="border-blue-500 text-blue-600">BNPL</Badge>
                    </div>
                    {paymentMethod === 'mshwari' && (
                      <div className="mt-4 pt-4 border-t">
                        <div className="p-3 rounded-lg bg-blue-100/50 text-sm">
                          <div className="flex items-center gap-2 text-blue-700 font-medium mb-1">
                            <Shield className="h-4 w-4" />
                            M-Shwari Loan
                          </div>
                          <p className="text-blue-600">
                            Get a loan of {formatCurrency(finalTotal)} and repay {formatCurrency(finalTotal * 1.075)} in 30 days (7.5% facility fee)
                          </p>
                        </div>
                        <Input 
                          placeholder="+254 7XX XXX XXX"
                          value={mpesaPhone}
                          onChange={e => setMpesaPhone(e.target.value)}
                          className="mt-3"
                        />
                      </div>
                    )}
                  </div>

                  {/* Jambo Wallet */}
                  <div className={`p-4 rounded-xl border-2 cursor-pointer ${paymentMethod === 'wallet' ? 'border-cyan-500 bg-cyan-50' : 'border-border'}`}>
                    <div className="flex items-center gap-4">
                      <RadioGroupItem value="wallet" id="wallet" />
                      <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center">
                        <Wallet className="h-6 w-6 text-cyan-600" />
                      </div>
                      <div className="flex-1">
                        <Label htmlFor="wallet" className="font-medium cursor-pointer">Jambo Wallet</Label>
                        <p className="text-sm text-muted-foreground">Balance: {formatCurrency(user?.wallet_balance || 0)}</p>
                      </div>
                      {(user?.wallet_balance || 0) < finalTotal && (
                        <Badge variant="outline" className="border-red-500 text-red-600">Low Balance</Badge>
                      )}
                    </div>
                  </div>

                  {/* Card */}
                  <div className={`p-4 rounded-xl border-2 cursor-pointer ${paymentMethod === 'card' ? 'border-purple-500 bg-purple-50' : 'border-border'}`}>
                    <div className="flex items-center gap-4">
                      <RadioGroupItem value="card" id="card" />
                      <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                        <CreditCard className="h-6 w-6 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <Label htmlFor="card" className="font-medium cursor-pointer">Debit/Credit Card</Label>
                        <p className="text-sm text-muted-foreground">Visa, Mastercard, Amex</p>
                      </div>
                    </div>
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary - Sticky */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24" data-testid="order-summary">
              <CardHeader>
                <CardTitle className="text-lg">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>{formatCurrency(cartData?.subtotal || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Delivery</span>
                    <span>{deliveryType === 'pickup' || cartData?.subtotal > 5000 ? <span className="text-green-600">FREE</span> : formatCurrency(cartData?.delivery_fee || 0)}</span>
                  </div>
                  {useFinancing && selectedFinancing && (
                    <div className="flex justify-between text-orange-600">
                      <span>Interest ({selectedFinancing.months} mo)</span>
                      <span>+{formatCurrency(selectedFinancing.interest_amount)}</span>
                    </div>
                  )}
                  {paymentMethod === 'mshwari' && (
                    <div className="flex justify-between text-blue-600">
                      <span>M-Shwari Fee (7.5%)</span>
                      <span>+{formatCurrency(finalTotal * 0.075)}</span>
                    </div>
                  )}
                </div>
                
                <Separator />
                
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="jambo-gradient-text">
                    {formatCurrency(paymentMethod === 'mshwari' ? finalTotal * 1.075 : finalTotal)}
                  </span>
                </div>

                {useFinancing && selectedFinancing && (
                  <div className="p-3 rounded-xl bg-orange-50 text-center">
                    <p className="text-sm text-muted-foreground">Monthly Payment</p>
                    <p className="text-2xl font-bold text-orange-600">{formatCurrency(selectedFinancing.monthly_payment)}</p>
                    <p className="text-xs text-muted-foreground">for {selectedFinancing.months} months</p>
                  </div>
                )}

                {paymentMethod === 'mshwari' && (
                  <div className="p-3 rounded-xl bg-blue-50 text-center">
                    <p className="text-sm text-muted-foreground">Repay in 30 days</p>
                    <p className="text-2xl font-bold text-blue-600">{formatCurrency(finalTotal * 1.075)}</p>
                  </div>
                )}

                <Button 
                  onClick={handleCheckout}
                  disabled={processing || checkingEligibility}
                  className="w-full h-12 rounded-full jambo-gradient text-white text-lg"
                  data-testid="place-order-btn"
                >
                  {processing || checkingEligibility ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : isAuthenticated ? (
                    paymentMethod === 'mpesa' ? 'Pay with M-Pesa' :
                    paymentMethod === 'mshwari' ? 'Get M-Shwari Loan' :
                    `Pay ${formatCurrency(finalTotal)}`
                  ) : (
                    'Login to Checkout'
                  )}
                </Button>

                {!isAuthenticated && (
                  <p className="text-xs text-center text-muted-foreground">
                    You'll be redirected to login before completing your order
                  </p>
                )}

                <div className="flex items-center justify-center gap-4 pt-4 border-t">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/b/b5/M-PESA_LOGO-01.svg" alt="M-Pesa" className="h-6" />
                  <img src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg" alt="Visa" className="h-4" />
                  <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" className="h-6" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* M-Pesa Payment Modal */}
      <Dialog open={showMpesaModal} onOpenChange={setShowMpesaModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <img src="https://upload.wikimedia.org/wikipedia/commons/b/b5/M-PESA_LOGO-01.svg" alt="M-Pesa" className="h-6" />
              M-Pesa Payment
            </DialogTitle>
            <DialogDescription>
              Complete your payment of {formatCurrency(finalTotal)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6">
            {mpesaStatus === 'pending' && (
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <Smartphone className="h-10 w-10 text-green-600 animate-pulse" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Check your phone</h3>
                <p className="text-muted-foreground mb-4">
                  An M-Pesa prompt has been sent to<br/>
                  <strong>{mpesaPhone}</strong>
                </p>
                <p className="text-sm text-muted-foreground">
                  Enter your M-Pesa PIN to complete the payment
                </p>
                <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Waiting for confirmation...
                </div>
              </div>
            )}
            
            {mpesaStatus === 'completed' && (
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-10 w-10 text-green-600" />
                </div>
                <h3 className="font-semibold text-lg mb-2 text-green-600">Payment Successful!</h3>
                <p className="text-muted-foreground">
                  Your order is being processed
                </p>
              </div>
            )}
            
            {mpesaStatus === 'failed' && (
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="h-10 w-10 text-red-600" />
                </div>
                <h3 className="font-semibold text-lg mb-2 text-red-600">Payment Failed</h3>
                <p className="text-muted-foreground mb-4">
                  The transaction was not completed
                </p>
                <Button onClick={() => { setShowMpesaModal(false); setMpesaStatus('pending'); }} variant="outline">
                  Try Again
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* M-Shwari Loan Modal */}
      <Dialog open={showMshwariModal} onOpenChange={setShowMshwariModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="h-6 w-6 text-blue-600" />
              M-Shwari Loan
            </DialogTitle>
            <DialogDescription>
              Buy now, pay later with M-Shwari
            </DialogDescription>
          </DialogHeader>
          
          {mshwariEligibility && (
            <div className="py-4 space-y-4">
              {mshwariEligibility.eligible ? (
                <>
                  <div className="p-4 rounded-xl bg-green-50 border border-green-200 text-center">
                    <CheckCircle2 className="h-8 w-8 text-green-600 mx-auto mb-2" />
                    <p className="font-semibold text-green-700">You're eligible!</p>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between p-3 rounded-lg bg-muted">
                      <span className="text-muted-foreground">Loan Amount</span>
                      <span className="font-semibold">{formatCurrency(mshwariEligibility.requested_amount)}</span>
                    </div>
                    <div className="flex justify-between p-3 rounded-lg bg-muted">
                      <span className="text-muted-foreground">Facility Fee ({mshwariEligibility.interest_rate})</span>
                      <span className="font-semibold">{formatCurrency(mshwariEligibility.facility_fee)}</span>
                    </div>
                    <div className="flex justify-between p-3 rounded-lg bg-blue-50">
                      <span className="font-medium">Total to Repay</span>
                      <span className="font-bold text-blue-600">{formatCurrency(mshwariEligibility.total_repayment)}</span>
                    </div>
                    <div className="flex justify-between p-3 rounded-lg bg-muted">
                      <span className="text-muted-foreground">Due Date</span>
                      <span className="font-semibold">{mshwariEligibility.due_date}</span>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={requestMshwariLoan} 
                    className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={processing}
                  >
                    {processing ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Confirm & Get Loan'}
                  </Button>
                  
                  <p className="text-xs text-center text-muted-foreground">
                    By confirming, you agree to M-Shwari terms and conditions
                  </p>
                </>
              ) : (
                <div className="text-center py-4">
                  <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="h-8 w-8 text-red-600" />
                  </div>
                  <h3 className="font-semibold mb-2">Not Eligible</h3>
                  <p className="text-muted-foreground">{mshwariEligibility.message}</p>
                  <Button onClick={() => setShowMshwariModal(false)} variant="outline" className="mt-4">
                    Choose Another Payment Method
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
